import { useEffect, useRef, useState } from "react";
import { beginInteraction, endInteraction, setDiagModule, diagApi, startDiag } from "@/lib/diag";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api, { apiError } from "@/lib/api";
import { getDeviceToken, setDeviceToken } from "@/lib/device";
import { useAuth } from "@/context/AuthContext";
import PinPad from "@/components/PinPad";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, ScanLine, Search, UserRoundCog, KeyRound } from "lucide-react";

const ROLE_CLS = { owner: "bg-violet-500/15 text-violet-500", manager: "bg-sky-500/15 text-sky-500", cashier: "bg-emerald-500/15 text-emerald-500" };
const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// Quick Login / Lock screen for trusted terminals. Also used as the lock overlay (mode="lock").
export default function QuickLogin({ mode = "login", lockedUser = null, onUnlocked }) {
  const nav = useNavigate();
  const { setSession, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(lockedUser || null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockUntil, setLockUntil] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!lockUntil) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockUntil]);
  const secsLeft = lockUntil ? Math.max(0, Math.ceil((new Date(lockUntil).getTime() - now) / 1000)) : 0;
  useEffect(() => { if (lockUntil && secsLeft === 0) { setLockUntil(null); setErr(""); } }, [secsLeft, lockUntil]);
  const countdown = `${Math.floor(secsLeft / 60)}:${String(secsLeft % 60).padStart(2, "0")}`;
  const [badge, setBadge] = useState("");
  const badgeRef = useRef(null);
  const token = getDeviceToken();

  useEffect(() => {
    // Header token = legacy/manual trust; registered POS terminals are recognised server-side from the HttpOnly register cookie.
    const t0 = performance.now();
    axios.get(`${BASE}/auth/quick/users`, { headers: token ? { "X-Device-Token": token } : {}, withCredentials: true })
      .then((r) => { diagApi("GET", `${BASE}/auth/quick/users`, 200, performance.now() - t0, false, "", null, "lock_login"); setData(r.data); })
      .catch((e) => { const reason = e.response?.data?.detail?.reason; diagApi("GET", `${BASE}/auth/quick/users`, e.response?.status || 0, performance.now() - t0, true, "", null, "lock_login", reason); if (e.response?.status === 401) { setDeviceToken(""); } setErr(apiError(e.response?.data?.detail)); });
  }, [token]);

  useEffect(() => { startDiag(); setDiagModule("lock_login"); return () => setDiagModule(null); }, []);   // lock screen has no user session yet: events wait in the buffer and are sent after unlock
  const submit = async () => {
    if (!sel || pin.length < 4 || lockUntil) return;
    setBusy(true); setErr("");
    const cid = beginInteraction("lock_login", "quick_login"); const t0 = performance.now();
    try {
      const { data: s } = await axios.post(`${BASE}/auth/quick/login`, { device_token: token, user_id: sel.id, pin, switch_from: lockedUser?.id || null, cid }, { withCredentials: true });
      diagApi("POST", `${BASE}/auth/quick/login`, 200, performance.now() - t0, false, "", cid, "lock_login", (lockedUser?.id && lockedUser.id !== sel.id) ? "user_switch_ok" : "login_ok");
      const u = await setSession(s.token, s.user);
      setPin("");
      endInteraction(cid, true);
      if (onUnlocked) onUnlocked(u); else nav("/");
    } catch (e) {
      const st = e.response?.status || 0;
      const reason = e.response?.data?.detail?.reason;
      diagApi("POST", `${BASE}/auth/quick/login`, st, performance.now() - t0, true, st ? "" : "network", cid, "lock_login", reason);   // 401/429 = expected PIN responses (classified server-side), never a software failure; no PIN or user data recorded
      const d = e.response?.data?.detail;
      if (e.response?.status === 429 && d?.retry_at) { setLockUntil(d.retry_at); setErr(""); }
      else setErr(apiError(d));
      setPin("");
    }
    finally { setBusy(false); }
  };
  const scanBadge = async (e) => {
    e.preventDefault();
    if (!badge.trim()) return;
    setErr("");
    try {
      const { data: r } = await axios.post(`${BASE}/auth/quick/badge`, { device_token: token, badge: badge.trim() }, { withCredentials: true });
      setSel(r.user); setBadge("");
    } catch (er) { setErr(apiError(er.response?.data?.detail)); setBadge(""); }
  };
  const users = (data?.users || []).filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.role.includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="quick-login-screen">
      <Card className="w-full max-w-3xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs font-bold tracking-widest text-muted-foreground flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {mode === "lock" ? "REGISTER LOCKED" : "NEEC POS QUICK LOGIN"}</p>
            <h1 className="font-head text-3xl font-extrabold">{data?.business?.name || "NEEC POS"}: {sel ? `Hello, ${sel.name.split(" ")[0]}` : "Select User"}</h1>
            {data?.device?.name && <p className="text-xs text-muted-foreground">Trusted terminal: {data.device.name}</p>}
          </div>
          {sel && <Button variant="outline" className="h-12 px-5 text-base border-violet-500/50 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10" onClick={() => { setSel(null); setPin(""); setErr(""); }} data-testid="quick-back"><UserRoundCog className="w-5 h-5 mr-2" /> Switch User</Button>}
        </div>

        {!sel ? (
          <>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9" placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quick-search" /></div>
              {data?.badge_login_enabled && (
                <form onSubmit={scanBadge} className="relative w-64">
                  <ScanLine className="w-4 h-4 absolute left-3 top-2.5 text-sky-500" />
                  <Input ref={badgeRef} className="pl-9 mono" placeholder="Scan Staff ID…" value={badge} onChange={(e) => setBadge(e.target.value)} autoFocus data-testid="quick-badge-input" />
                </form>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[46vh] overflow-y-auto pr-1" data-testid="quick-user-grid">
              {users.map((u) => (
                <button key={u.id} type="button" onClick={() => { setSel(u); setErr(""); }} data-testid={`quick-user-${u.id}`}
                  className="rounded-xl border border-border p-4 text-left hover:border-violet-500 hover:bg-violet-500/5 transition-colors">
                  <div className="w-11 h-11 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold mb-2">{u.initials}</div>
                  <p className="font-semibold truncate">{u.name}</p>
                  <Badge className={`text-[10px] capitalize ${ROLE_CLS[u.role] || ""}`}>{u.role}</Badge>
                  {!u.has_login_pin && <p className="text-[10px] text-amber-600 mt-1">No login PIN yet</p>}
                </button>
              ))}
              {data && users.length === 0 && <p className="col-span-full text-center text-muted-foreground py-8">No matching staff.</p>}
            </div>
          </>
        ) : (
          <div className="max-w-xs mx-auto">
            <div className="flex items-center gap-3 mb-2 justify-center">
              <div className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">{sel.initials}</div>
              <div><p className="font-semibold leading-tight">{sel.name}</p><p className="text-xs text-muted-foreground capitalize">{sel.role} · enter your login PIN</p></div>
            </div>
            {lockUntil ? (
              <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 text-center" data-testid="quick-lockout">
                <p className="font-semibold text-amber-700 dark:text-amber-400">Too many incorrect PINs</p>
                <p className="text-3xl font-head font-extrabold mono my-2" data-testid="quick-lockout-countdown">{countdown}</p>
                <p className="text-xs text-muted-foreground">Quick Login for {sel.name.split(" ")[0]} is paused. Other staff can still sign in. Use email &amp; password below to sign in now. A successful full login clears this pause.</p>
                <Button variant="outline" className="mt-3" onClick={() => { setSel(null); setPin(""); setLockUntil(null); }} data-testid="quick-lockout-switch">Switch User</Button>
              </div>
            ) : (
              <PinPad pin={pin} setPin={setPin} onSubmit={submit} onCancel={() => { setSel(null); setPin(""); }} error={err} busy={busy} submitLabel="Sign in" testPrefix="quick-pin" />
            )}
          </div>
        )}
        {!sel && err && <p className="text-center text-sm text-rose-500 mt-3" data-testid="quick-error">{err}</p>}

        <div className="flex items-center justify-between mt-6 text-xs text-muted-foreground">
          <button type="button" className="hover:underline flex items-center gap-1" onClick={async () => { sessionStorage.setItem("neec_full_login", "1"); await logout(); nav("/login"); }} data-testid="quick-full-login"><KeyRound className="w-3.5 h-3.5" /> Use email &amp; password instead</button>
          {lockedUser?.shift_open && <span className="text-amber-600">{lockedUser.name} has an open shift on this register</span>}
        </div>
      </Card>
    </div>
  );
}
